<template>
    <div>
        <div v-if="googleBusiness">
            <div v-if="googleBusiness.invalid_refresh_token">
                <div class="alert alert-warning" role="alert">
                    {{ __('Your Google Business connection is no longer valid. Please reconnect your account.') }}
                </div>
                <div class="d-flex justify-content-between align-items-center">
                    <div id="google-login-button" v-show="!loggedIn" class="btn btn-primary social-login google-login"
                        @click="() => handleAuthClick(true)">
                        <i class="fab fa-google"></i> Reconnect Account
                    </div>
                    <button class="btn btn-danger" @click="deleteBusiness">Delete</button>
                </div>
            </div>
            <div v-else>
                <p class="mb-4">You have connected the account <strong>{{ googleBusiness.display_name }}</strong>.</p>
                <div class="btn-toolbar d-flex justify-content-end" role="group" aria-label="">
                    <button class="btn btn-danger" @click="deleteBusiness">Delete</button>&nbsp;
                    <a v-show="next_url" class="btn btn-primary" :href="next_url">Continue</a>
                </div>
            </div>
        </div>

        <div class="google-connect" v-if="!googleBusiness">
            <h2 class="mb-3 thin-title">Connect your Google My Business account</h2>
            <div id="google-login-button" v-show="!loggedIn" class="btn btn-primary social-login google-login mb-3" @click="() => handleAuthClick(false)">
                <i class="fab fa-google"></i> Connect with Google
            </div>
            <div class="select-google-business">
                <select class="form-control mb-3" ref="locations" v-show="locations.length > 0">
                    <option v-for="location in locations" :value="location.name">{{ location.title }}</option>
                </select>
                <div class="btn-toolbar" role="group" aria-label="">
                    <button v-show="locations.length > 0" class="btn btn-primary" @click="saveLocation">Save</button>
                    <a v-show="use_skip" class="blue-link" :href="next_url">SKIP THIS STEP</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'GoogleSettings',
    props: ['team_id', 'google_business', 'use_skip', 'next_url'],

    data() {
        return {
            clientId: '702026261134-2mabufer8bl5gfmc7sv9h89d5h3ovalv.apps.googleusercontent.com',
            scopes: 'https://www.googleapis.com/auth/business.manage',
            loggedIn: false,
            accounts: [],
            locations: [],
            googleBusiness: null,
            setUp: false,
            oauthToken: '',
            tempAccessToken: '',
            fullTokenResponse: null,
            codeClient: null,
            isReconnecting: false,
        };
    },

    mounted() {
        this.googleBusiness = this.google_business;
        const sparkGoogleBusinessInvalid = Spark?.googleBusiness?.invalid_refresh_token;

        if (!this.googleBusiness || sparkGoogleBusinessInvalid) {
            this.googleBusiness = this.googleBusiness || Spark.googleBusiness;
            this.prepareSetup();
        }
    },

    methods: {
        prepareSetup() {
            if (!this.setUp) {
                let googleApi = document.createElement('script');
                googleApi.setAttribute('src', 'https://accounts.google.com/gsi/client');
                googleApi.setAttribute('async', '');
                googleApi.setAttribute('defer', '');
                document.head.appendChild(googleApi);
                googleApi.onload = () => {
                    this.initClient();
                    this.setUp = true;
                };
                googleApi.onerror = (e) => {
                    console.error('Failed to load Google Identity Services script', e);
                    swal(
                        'Error',
                        'Failed to load Google authentication services. Please try again.',
                        'error',
                    );
                };
            } else {
                this.initClient();
            }
        },

        initClient() {
            this.codeClient = google.accounts.oauth2.initCodeClient({
                client_id: this.clientId,
                scope: this.scopes,
                ux_mode: 'popup',
                prompt: 'consent',
                access_type: 'offline',
                callback: (response) => {
                    if (response.code) {
                        this.oauthToken = response.code;
                        this.loggedIn = true;
                        this.prepareAccounts();
                    } else {
                        console.error('Authorization failed', response);
                        swal(
                            'Authorization Failed',
                            'Could not connect to Google. Please try again.',
                            'error',
                        );
                    }
                },
            });
        },

        handleAuthClick(reconnect = false) {
            this.isReconnecting = reconnect;
            if (this.codeClient) {
                this.codeClient.requestCode();
            } else {
                console.error('Google Identity Services client not initialized.');
                swal(
                    'Error',
                    'Google authentication not ready. Please refresh and try again.',
                    'error',
                );
            }
        },

        async prepareAccounts() {
            try {
                const response = await axios.post('/api/google-accounts', {
                    oauth_code: this.oauthToken,
                });

                if (response.data.status === 'success') {
                    this.accounts = response.data.accounts;
                    this.tempAccessToken = response.data.temp_access_token;
                    this.fullTokenResponse = response.data.oauth_token;

                    if (this.accounts.length > 0) {
                        await this.getLocations(this.accounts[0].name); // Ensure locations are fetched

                        // Check if reconnecting and if the original connected location is among the fetched locations
                        if (this.isReconnecting && this?.googleBusiness?.location_name) {
                            const foundLocation = this.locations.find(
                                loc => loc.name === 'locations\/' + this.googleBusiness.location_name
                            );

                            if (!foundLocation) {
                                swal(
                                    'Location Access Lost',
                                    'The reconnected Google account no longer has access to the previously connected Google My Business location.\n\n' +
                                    'You may need to connect a different Google account or re-add access for this account to the location.',
                                    'warning',
                                );

                                this.isReconnecting = false;
                                this.loggedIn = false;

                                return;
                            }
                        }

                        // If reconnecting and locations are found (and access confirmed), automatically submit
                        if (this.isReconnecting && this.locations.length > 0) {
                            this.submitConnection();
                        }
                    } else {
                        swal(
                            'No Accounts Found',
                            'No Google My Business accounts were found for your Google profile.',
                            'info',
                        );
                    }
                } else {
                    console.error(
                        'Error from backend while fetching accounts:',
                        response.data.message,
                    );
                    swal('Error', response.data.message, 'error');
                }
            } catch (error) {
                console.error('Error calling backend for accounts:', error);
                swal(
                    'Error',
                    'Failed to retrieve Google My Business accounts. Please try again.',
                    'error',
                );
            }
        },

        async getLocations(accountName) {
            try {
                const response = await axios.post('/api/google-locations', {
                    temp_access_token: this.tempAccessToken,
                    account_name: accountName,
                });

                if (response.data.status === 'success') {
                    this.locations = response.data.locations;
                    if (this.locations.length === 0) {
                        swal(
                            'No Locations Found',
                            'No locations found for the selected Google My Business account.',
                            'info',
                        );
                    }
                } else {
                    console.error(
                        'Error from backend while fetching locations:',
                        response.data.message,
                    );
                    swal('Error', response.data.message, 'error');
                }
            } catch (error) {
                console.error('Error calling backend for locations:', error);
                swal(
                    'Error',
                    'Failed to retrieve Google My Business locations. Please try again.',
                    'error',
                );
            }
        },

        submitConnection() {
            if (this.isReconnecting) {
                this.updateBusinessConnection();
            } else {
                this.saveLocation();
            }
        },

        saveLocation() {
            const selectedLocationRaw = this.$refs.locations.value;

            const fullAccountName = this.accounts[0].name;

            const locationIdParts = selectedLocationRaw.split('/');
            const locationId = locationIdParts[1];
            const fullResourceName = `${fullAccountName}/${locationIdParts[0]}/${locationId}`;

            if (!selectedLocationRaw) {
                swal(
                    'Selection Required',
                    'Please select a Google My Business location.',
                    'warning',
                );
                return;
            }

            const selectedLocationData = this.locations.find(
                (loc) => loc.name === selectedLocationRaw,
            );

            if (!selectedLocationData) {
                swal('Error', 'Selected location data not found.', 'error');
                return;
            }

            const data = {
                display_name: selectedLocationData.title,
                names: fullResourceName,
                oauth_token: this.fullTokenResponse,
            };

            axios
                .post('/api/settings/' + this.team_id + '/google', data)
                .then((response) => {
                    const data = response.data;
                    if (data.status === 'success') {
                        this.googleBusiness = data.google_business;
                        this.isReconnecting = false;
                        swal('Congrats!', data.message, 'success');
                        this.$eventHub.$emit('channelEnabled', 'google');
                    } else {
                        swal('Hmmm...', data.message, 'error');
                    }
                })
                .catch((error) => {
                    console.error('Error saving location:', error);
                    swal(
                        'Error',
                        'Failed to save Google Business location. Please try again.',
                        'error',
                    );
                });
        },

        async updateBusinessConnection() {
            if (!this.googleBusiness || !this.googleBusiness.id) {
                swal('Error', 'No Google Business connection found to update.', 'error');
                return;
            }

            const data = {
                oauth_token: this.fullTokenResponse,
            };

            try {
                const response = await axios.put(
                    `/api/settings/${this.team_id}/google/${this.googleBusiness.id}/reconnect`,
                    data
                );

                if (response.data.status === 'success') {
                    this.googleBusiness = response.data.google_business;
                    this.isReconnecting = false;
                    this.loggedIn = true;
                    swal('Reconnected!', response.data.message, 'success');
                } else {
                    swal('Error', response.data.message, 'error');
                }
            } catch (error) {
                console.error('Error updating business connection:', error);
                swal(
                    'Error',
                    'Failed to reconnect Google Business account. Please try again.',
                    'error',
                );
            }
        },

        deleteBusiness() {
            axios.delete(`/api/settings/${this.team_id}/google/${this.googleBusiness.id}`).then(
                (response) => {
                    if (response.data.status === 'success') {
                        this.$eventHub.$emit('channelDisabled', 'google');
                        this.googleBusiness = undefined;
                        this.locations = [];
                        this.loggedIn = false;
                        this.oauthToken = '';
                        this.tempAccessToken = '';
                        this.fullTokenResponse = null;
                        this.setUp = false;
                        this.isReconnecting = false;
                        this.prepareSetup();
                        swal('Disconnected!', 'Google account has been disconnected.', 'success');
                    } else {
                        swal('Error', 'Failed to disconnect Google account.', 'error');
                    }
                },
                (error) => {
                    console.error('Error deleting business:', error);
                    swal('Error', 'Failed to disconnect Google account.', 'error');
                },
            );
        },
    },
};
</script>